import { Box, Button, Card, Flex, Spinner, Text, Toast, Tooltip, useToast } from '@chakra-ui/react';
import { useContext, useMemo } from 'react';
import StepperRentals from './StepperRentals';
import PaymentSummary from './PaymentSummary';
import PaymentMethodSelection from './PaymentMethodSelection';
import PaymentForm from './PaymentForm';
import StepperRestingStep from './StepperRestingStep';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import { useCaptcha } from '../hooks/useCaptcha';
import { usePayment } from '../hooks/usePayment';
import CustomButton from './CustomButton';
import StepperContext from '../contexts/StepperContext';
import { getPaymentSource } from '../helpers/paymentSource';
import { getBrowserData } from '../helpers/threeds';

const StepperContainer = (): JSX.Element => {
  const steps = ['Seleccionar factura', 'Método de pago', 'Confirmación de datos'];
  const { invoicesToPay, invoiceSelectionLocked } = useContext(InvoicesToPayContext);
  const { showSubscriptionOffer, selectedSubscription } = useContext(SubscriptionsContext);
  const toast = useToast();
  const { activeStepContext, paymentMethodContext, paymentInformationContext } = useContext(StepperContext);
  const { activeStep, setActiveStep } = activeStepContext;
  const { paymentMethod, setPaymentMethod } = paymentMethodContext;
  const { paymentInformation } = paymentInformationContext;

  const payObject = useMemo(() => {
    if (paymentMethod?.id === 'pse' && paymentInformation.pseBank)
      return { type: 'pse' as const, bank: paymentInformation.pseBank };
    if (paymentMethod?.id === 'nequiButton' && paymentInformation.phoneNumber)
      return { type: 'nequiButton' as const, phoneNumber: paymentInformation.phoneNumber };
    const browserData = getBrowserData();
    if (
      paymentMethod?.id === 'card' &&
      browserData &&
      paymentInformation.cardCVC &&
      paymentInformation.cardExpDate &&
      paymentInformation.cardNumber
    )
      return {
        type: 'card' as const,
        card: {
          cardNumber: paymentInformation.cardNumber,
          expirationMonth: paymentInformation.cardExpDate.substring(0, 2),
          expirationYear: paymentInformation.cardExpDate.slice(-2),
          cvv: paymentInformation.cardCVC,
        },
        browserData,
        installments: paymentInformation.installments,
      };
    if (paymentMethod?.id === 'breb') return { type: 'breb' as const };
    if (paymentMethod?.id === 'cash') return { type: 'cash' as const };
    if (paymentMethod?.id === 'bancolombiaButton') return { type: 'bancolombiaButton' as const };
  }, [paymentMethod, paymentInformation]);

  const payerInfo = useMemo(() => {
    if (
      paymentInformation.name &&
      paymentInformation.documentType &&
      paymentInformation.documentNumber &&
      paymentInformation.email
    )
      return {
        name: paymentInformation.name,
        documentType: paymentInformation.documentType,
        documentNumber: paymentInformation.documentNumber,
        email: paymentInformation.email,
      };
  }, [paymentInformation]);

  const puntosColombia = useMemo(() => {
    if (paymentInformation.puntosColombiaDocumentType && paymentInformation.puntosColombiaDocumentNumber)
      return {
        documentType: paymentInformation.puntosColombiaDocumentType,
        documentNumber: paymentInformation.puntosColombiaDocumentNumber,
      };
  }, [paymentInformation]);

  // The challenge runs on the confirmation step, the one that submits the
  // payment, so an interactive one lands next to the button it blocks.
  const captcha = useCaptcha({ enabled: activeStep >= 3 });

  const { processPayment } = usePayment(captcha, () => {
    toast({
      title: 'Error generando pago',
      description:
        'Hubo un error al generar tu pago. Refresca la página e intentalo de nuevo. Si el error persiste, comunícate con nosotros.',
      status: 'error',
      duration: 6000,
      render: (props) => (
        <Box onClick={() => window.location.reload()} cursor={'pointer'}>
          <Toast {...props} />
        </Box>
      ),
    });
  });

  const nextStep = async () => {
    if (activeStep === 3 && payObject && payerInfo)
      processPayment(payObject, payerInfo, getPaymentSource() ?? 'portal', puntosColombia);
    else setActiveStep(activeStep + 1);
  };

  const previousStep = () => {
    if (activeStep === 1 && paymentMethod) setPaymentMethod(undefined);

    if (activeStep > -1) setActiveStep(activeStep - 1);
  };

  const isButtonDisabled = useMemo(() => {
    if (activeStep === 1 && !paymentMethod) return true;
    if (activeStep === 2 && !paymentInformation.okay) return true;
    if (activeStep === 3 && captcha.status !== 'ready') return true;
    return false;
  }, [paymentMethod, activeStep, paymentInformation, captcha.status]);

  return (
    <Card width="100%" alignContent="center" justifyContent="center" gap={4} p={4}>
      <Box margin={'auto'}>
        <StepperRentals steps={steps} activeStep={activeStep} nextStep={nextStep} previousStep={previousStep} />
      </Box>

      {invoiceSelectionLocked ? (
        <Box height={'320px'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
          <Spinner />
        </Box>
      ) : (
        <Box>
          {activeStep === -1 && <StepperRestingStep />}
          {activeStep === 0 && <PaymentSummary />}
          {activeStep === 1 && <PaymentMethodSelection />}
          {activeStep === 2 && <PaymentForm />}
          {activeStep === 3 && <PaymentSummary />}
        </Box>
      )}

      <Box display="flex" flexDirection={'column'} alignItems={'center'} py={4}>
        {/* Turnstile only paints inside this box when it needs the visitor to do
        something; the rest of the time it takes no space. */}
        <Box ref={captcha.containerRef} width={'100%'} maxWidth={'500px'} />

        {activeStep === 3 && captcha.status === 'error' && (
          <Text fontSize={'sm'} color={'red.500'} textAlign={'center'} pb={2}>
            No pudimos verificar tu seguridad.{' '}
            <Button variant={'link'} fontSize={'sm'} colorScheme={'red'} onClick={captcha.reset}>
              Reintentar
            </Button>
          </Text>
        )}

        <Box display="flex" flexDirection={'row'} justifyContent={'center'} width={'100%'}>
          {activeStep === 0 && (
            <Tooltip
              label={
                showSubscriptionOffer && selectedSubscription === null
                  ? 'Indica si quieres o no recibir beneficios para continuar'
                  : undefined
              }
              hasArrow
              placement="top"
            >
              <Box as="span" display="inline-block">
                <CustomButton
                  onClick={nextStep}
                  isDisabled={invoicesToPay.length < 1 || (showSubscriptionOffer && selectedSubscription === null)}
                >
                  INICIAR PAGO
                </CustomButton>
              </Box>
            </Tooltip>
          )}
          {activeStep > 0 && (
            <Flex width="100%" maxWidth="500px" gap={2}>
              <CustomButton onClick={previousStep} isDisabled={activeStep === 0 || invoiceSelectionLocked} width={'50%'}>
                REGRESAR
              </CustomButton>
              <Tooltip
                label={
                  activeStep === 3 && captcha.status === 'solving'
                    ? 'Estamos verificando tu seguridad, esto toma un momento'
                    : undefined
                }
                hasArrow
                placement="top"
              >
                <Box as="span" display="inline-block" width={'50%'}>
                  <CustomButton
                    onClick={nextStep}
                    isDisabled={isButtonDisabled || activeStep === steps.length + 1 || invoiceSelectionLocked}
                    width={'100%'}
                  >
                    {activeStep > 2 ? 'CONFIRMAR' : 'CONTINUAR'}
                  </CustomButton>
                </Box>
              </Tooltip>
            </Flex>
          )}
        </Box>
      </Box>

      <div id="ThreedsmethodFrameContainer" style={{ display: 'none' }}></div>
    </Card>
  );
};

export default StepperContainer;
